import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Skeleton,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  FormatListBulleted as WineListIcon,
  Inventory as InventoryIcon,
  ShoppingCart as PurchasingIcon,
  Checklist as StockCountIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useVenue } from '../../../context/VenueContext';
import api from '../../../services/api';
import inventoryItemService from '../../../services/inventoryItemService';
import purchaseOrderService from '../../../services/purchaseOrderService';
import stockCountService from '../../../services/stockCountService';
import wineListService from '../../../services/wineListService';
import venueLocationService from '../../../services/venueLocationService';
import VenueLogoPanel from '../../../components/venue/VenueLogoPanel';

// ── Types ─────────────────────────────────────────────────────────────────────

interface AlertSummary {
  negativeStock: number;
  outOfStock: number;
  lowStock: number;
}

interface StatCardProps {
  label: string;
  icon: React.ReactNode;
  value: number | string | null;
  loading: boolean;
  actionLabel: string;
  actionPath: string;
  color: string;
}

// ── StatCard ──────────────────────────────────────────────────────────────────

const StatCard: React.FC<StatCardProps> = ({ label, icon, value, loading, actionLabel, actionPath, color }) => {
  const theme = useTheme();
  const navigate = useNavigate();

  return (
    <Paper
      sx={{
        p: 2.5,
        borderRadius: 2,
        boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        flex: '1 1 200px',
        minWidth: 180,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="caption" fontWeight={600} sx={{ color: theme.palette.text.secondary, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {label}
        </Typography>
        <Box sx={{ color, opacity: 0.7 }}>{icon}</Box>
      </Box>
      {loading ? (
        <Skeleton variant="text" width={80} height={56} />
      ) : (
        <Typography
          variant={typeof value === 'string' ? 'h5' : 'h4'}
          fontWeight={700}
          sx={{ color: theme.palette.text.primary, lineHeight: 1.2 }}
        >
          {value ?? '—'}
        </Typography>
      )}
      <Button
        size="small"
        onClick={() => navigate(actionPath)}
        sx={{ textTransform: 'none', alignSelf: 'flex-start', color, p: 0, minWidth: 0, fontWeight: 600 }}
      >
        {actionLabel} →
      </Button>
    </Paper>
  );
};

// ── VenueDashboard ────────────────────────────────────────────────────────────

const VenueDashboard: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { venueId, venueName } = useVenue();
  const base = `/admin/venues/${venueId}`;

  const [loading, setLoading] = useState(true);
  const [alertSummary, setAlertSummary] = useState<AlertSummary | null>(null);
  const [wineListCount, setWineListCount] = useState<number | null>(null);
  const [inventoryCount, setInventoryCount] = useState<number | null>(null);
  const [openPoCount, setOpenPoCount] = useState<number | null>(null);
  const [lastCountDate, setLastCountDate] = useState<string | null>(null);
  const [locationCount, setLocationCount] = useState<number | null>(null);

  useEffect(() => {
    if (!venueId) return;

    const load = async () => {
      setLoading(true);
      const [lists, inventory, orderedPos, partialPos, counts, locations, alerts] = await Promise.allSettled([
        wineListService.getWineLists(venueId, 1, 1),
        inventoryItemService.getItems(venueId, 1, 1),
        purchaseOrderService.getOrders(venueId, 1, 1, 'ordered'),
        purchaseOrderService.getOrders(venueId, 1, 1, 'partially_received'),
        stockCountService.getCounts(venueId, 1, 1, 'posted'),
        venueLocationService.getLocations(venueId),
        api.get(`/admin/venue/${venueId}/inventory/alerts`),
      ]);

      if (lists.status === 'fulfilled') setWineListCount(lists.value.pagination.totalItems);
      if (inventory.status === 'fulfilled') setInventoryCount(inventory.value.pagination.totalItems);
      if (orderedPos.status === 'fulfilled' && partialPos.status === 'fulfilled') {
        setOpenPoCount(orderedPos.value.pagination.totalItems + partialPos.value.pagination.totalItems);
      }
      if (counts.status === 'fulfilled') {
        const latest = counts.value.data?.[0];
        setLastCountDate(
          latest?.countedAt
            ? new Date(latest.countedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
            : 'Never',
        );
      }
      if (locations.status === 'fulfilled') setLocationCount(locations.value.length);
      if (alerts.status === 'fulfilled') {
        const s = alerts.value.data.summary;
        if (s.negativeStock > 0 || s.outOfStock > 0 || s.lowStock > 0) {
          setAlertSummary({ negativeStock: s.negativeStock, outOfStock: s.outOfStock, lowStock: s.lowStock });
        }
      }

      setLoading(false);
    };

    load();
  }, [venueId]);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>{venueName}</Typography>
        <Typography variant="body2" color="text.secondary">
          Venue overview
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2.5 }}>
        <StatCard
          label="Wine Lists"
          icon={<WineListIcon />}
          value={wineListCount}
          loading={loading}
          actionLabel="Manage wine lists"
          actionPath={`${base}/wine-lists`}
          color={theme.palette.primary.main}
        />
        <StatCard
          label="Inventory Items"
          icon={<InventoryIcon />}
          value={inventoryCount}
          loading={loading}
          actionLabel="View inventory"
          actionPath={`${base}/inventory`}
          color={theme.palette.secondary.main}
        />
        <StatCard
          label="Open Purchase Orders"
          icon={<PurchasingIcon />}
          value={openPoCount}
          loading={loading}
          actionLabel="View purchasing"
          actionPath={`${base}/purchasing`}
          color="#9a6700"
        />
        <StatCard
          label="Last Stock Count"
          icon={<StockCountIcon />}
          value={lastCountDate}
          loading={loading}
          actionLabel="View counts"
          actionPath={`${base}/stock-counts`}
          color="#6e40c9"
        />
        <StatCard
          label="Locations"
          icon={<LocationIcon />}
          value={locationCount}
          loading={loading}
          actionLabel="Manage locations"
          actionPath={`${base}/locations`}
          color={theme.palette.text.secondary as string}
        />
        {/* W7A — venue logo card flows in the same grid as the StatCards.
            Visually a tile, not a settings panel. Last position in the row. */}
        {venueId && <VenueLogoPanel venueId={venueId} />}
      </Box>

      {alertSummary && (
        <Paper
          sx={{
            mt: 3,
            p: 2.5,
            borderRadius: 2,
            boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
            backgroundColor: alpha(theme.palette.warning.main, 0.07),
            border: `1px solid ${alpha(theme.palette.warning.main, 0.25)}`,
            maxWidth: 320,
          }}
        >
          <Typography
            variant="caption"
            fontWeight={600}
            sx={{ color: theme.palette.text.secondary, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', mb: 1.5 }}
          >
            Operational Alerts
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {alertSummary.negativeStock > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 4 }}>
                <Typography variant="body2" color="text.secondary">Negative stock</Typography>
                <Typography variant="body2" fontWeight={600}>{alertSummary.negativeStock}</Typography>
              </Box>
            )}
            {alertSummary.outOfStock > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 4 }}>
                <Typography variant="body2" color="text.secondary">Out of stock</Typography>
                <Typography variant="body2" fontWeight={600}>{alertSummary.outOfStock}</Typography>
              </Box>
            )}
            {alertSummary.lowStock > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 4 }}>
                <Typography variant="body2" color="text.secondary">Low stock</Typography>
                <Typography variant="body2" fontWeight={600}>{alertSummary.lowStock}</Typography>
              </Box>
            )}
          </Box>
          <Button
            size="small"
            onClick={() => navigate(`${base}/inventory/alerts`)}
            sx={{ textTransform: 'none', color: theme.palette.warning.dark, p: 0, minWidth: 0, fontWeight: 600, mt: 1.5 }}
          >
            View all →
          </Button>
        </Paper>
      )}
    </Box>
  );
};

export default VenueDashboard;
